/**
 * Boîte de réception : messages envoyés par les étudiants à la scolarité.
 * Charte sobre, cohérente avec MessagerieTab.
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Inbox, Search, Send, CheckCheck, Check, Archive, Loader2, Bell, X, Trash2,
  ChevronDown, ChevronRight, ImageIcon, Paperclip, Download, Play, Pause,
  Mic, Square, FileText, Calculator, RefreshCw, DollarSign, Unlock, Star,
  MoreHorizontal, Printer,
} from 'lucide-react';
import {
  useGetMessagesEtudiantsQuery,
  useMarquerMessageEtudiantLuMutation,
  useRepondreMessageEtudiantMutation,
  useArchiverMessageEtudiantMutation,
  useToggleImportantMessageEtudiantMutation,
  usePushTestMutation,
  useGetMessageEtudiantThreadQuery,
  useSupprimerPostMessageEtudiantMutation,
  MessageEtudiant,
} from '../../services/messagesEtudiantsApi';
import { useLazyGetEtudiantParMatriculeQuery } from '../../services/etudiantsApi';
import { useForcerAccesMutation } from '../../services/portailAdminApi';
import ActionRapideDeliberationModal from '../../components/Deliberation/ActionRapideDeliberationModal';

function StudentAvatar({ photoUrl, nom, size = 32 }: { photoUrl?: string | null; nom?: string; size?: number }) {
  const initials = (nom || '?').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        style={{ width: size, height: size }}
        className="rounded-full object-cover bg-gray-200 dark:bg-gray-700 flex-shrink-0"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      className="rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold flex items-center justify-center flex-shrink-0"
    >
      {initials || '?'}
    </div>
  );
}

const STATUTS = [
  { key: 'non_traites', label: 'À traiter' },
  { key: 'non_lus', label: 'Non lus' },
  { key: 'importants', label: 'Importants' },
  { key: 'traites', label: 'Traités' },
  { key: 'all', label: 'Tous' },
] as const;

type StatutKey = typeof STATUTS[number]['key'];

const MessagesEtudiantsView: React.FC = () => {
  const [statut, setStatut] = useState<StatutKey>('non_traites');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<MessageEtudiant | null>(null);
  const [reponse, setReponse] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Pièce jointe : image ou fichier sélectionné avant envoi
  const [fichierAttache, setFichierAttache] = useState<File | null>(null);
  const [fichierPreview, setFichierPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Lightbox pour vue plein écran d'une image (photo étudiant ou image envoyée)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Génère un preview local quand on attache une image
  useEffect(() => {
    if (!fichierAttache) {
      setFichierPreview(null);
      return;
    }
    if (fichierAttache.type.startsWith('image/')) {
      const url = URL.createObjectURL(fichierAttache);
      setFichierPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setFichierPreview(null);
  }, [fichierAttache]);

  const { data, isLoading, refetch } = useGetMessagesEtudiantsQuery({ statut, q: q || undefined, limit: 200 });
  const [marquerLu] = useMarquerMessageEtudiantLuMutation();
  const [repondre, { isLoading: envoyantReponse }] = useRepondreMessageEtudiantMutation();
  const [archiver] = useArchiverMessageEtudiantMutation();
  const [toggleImportant] = useToggleImportantMessageEtudiantMutation();
  const [forcerAcces, { isLoading: forcantAcces }] = useForcerAccesMutation();
  const [fetchEtudiant] = useLazyGetEtudiantParMatriculeQuery();
  const [pushTest, { isLoading: testing }] = usePushTestMutation();

  // ── Enregistrement vocal (MediaRecorder) ──
  const [recording, setRecording] = useState(false);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingStartedRef = useRef<number>(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                   MediaRecorder.isTypeSupported('audio/ogg') ? 'audio/ogg' : '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      recordedChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const dur = Math.round((Date.now() - recordingStartedRef.current) / 1000);
        const blob = new Blob(recordedChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const ext = (rec.mimeType || 'audio/webm').includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `vocal_${Date.now()}.${ext}`, { type: blob.type });
        setFichierAttache(file);
        setRecordedDuration(dur);
        stream.getTracks().forEach(t => t.stop());
      };
      recordingStartedRef.current = Date.now();
      rec.start();
      setRecording(true);
      setRecordedDuration(0);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => {
        setRecordedDuration(Math.round((Date.now() - recordingStartedRef.current) / 1000));
      }, 250);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Microphone refusé ou indisponible.' });
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    }
  };

  // Nettoyage : si on quitte la conversation pendant qu'on enregistre, on coupe.
  useEffect(() => {
    return () => {
      if (recorderRef.current && recording) {
        try { recorderRef.current.stop(); } catch { /* ignore */ }
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Quick actions ──
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [showActionRapide, setShowActionRapide] = useState(false);
  const [etudiantIdSelected, setEtudiantIdSelected] = useState<number | null>(null);
  const [showAccesModal, setShowAccesModal] = useState(false);
  const [raisonAcces, setRaisonAcces] = useState('');
  // Fix 2026-09-06 : mot de passe global de confirmation (défini par admin)
  const [mdpAcces, setMdpAcces] = useState('');
  // Fix 2026-09-04 : semestre choisi pour les actions (S1 impair / S2 pair)
  const [actionSemestre, setActionSemestre] = useState<1 | 2>(2);

  // Fix 2026-09-06 : labels absolus des semestres selon niveau étudiant
  // L1→S1/S2, L2→S3/S4, L3→S5/S6, M1→S7/S8, M2→S9/S10
  const labelSemestre = (niveau: string | undefined, semRel: 1 | 2): string => {
    const offsets: Record<string, number> = { L1: 0, L2: 2, L3: 4, M1: 6, M2: 8 };
    const offset = offsets[(niveau || '').toUpperCase()] ?? 0;
    return `S${offset + semRel}`;
  };

  // Lazy fetch etudiant_id à partir de matricule pour ActionRapideDeliberationModal
  const openActionRapide = async (semestre: 1 | 2) => {
    if (!selected) return;
    setShowQuickMenu(false);
    setActionSemestre(semestre);
    try {
      const r = await fetchEtudiant(selected.matricule).unwrap();
      if (r?.id) {
        setEtudiantIdSelected(r.id);
        setShowActionRapide(true);
      } else {
        setFeedback({ type: 'error', text: 'Étudiant introuvable.' });
      }
    } catch {
      setFeedback({ type: 'error', text: 'Impossible de charger l\'étudiant.' });
    }
  };

  const openReleve = (semestre?: 1 | 2) => {
    if (!selected) return;
    setShowQuickMenu(false);
    const semParam = semestre ? `&semestre=${semestre}` : '';
    window.open(`/releves/notes?matricule=${encodeURIComponent(selected.matricule)}${semParam}`, '_blank');
  };

  const openRachat = (semestre: 1 | 2) => {
    if (!selected) return;
    setShowQuickMenu(false);
    window.open(`/rachat-intelligent?matricule=${encodeURIComponent(selected.matricule)}&semestre=${semestre}`, '_blank');
  };

  const handleToggleImportant = async (m: MessageEtudiant) => {
    try {
      const r = await toggleImportant({ id: m.id }).unwrap();
      if (selected?.id === m.id) setSelected({ ...selected, est_important: r.est_important });
    } catch { /* ignore */ }
  };

  const handleForcerAcces = async () => {
    if (!selected) return;
    if (raisonAcces.trim().length < 5) {
      setFeedback({ type: 'error', text: 'Veuillez préciser une raison (5 caractères min).' });
      return;
    }
    if (!mdpAcces.trim()) {
      setFeedback({ type: 'error', text: 'Mot de passe de confirmation requis.' });
      return;
    }
    try {
      await forcerAcces({
        matricule: selected.matricule,
        activer: true,
        raison: raisonAcces.trim(),
        mot_de_passe: mdpAcces.trim(),
      } as any).unwrap();
      setShowAccesModal(false);
      setRaisonAcces('');
      setMdpAcces('');
      setFeedback({ type: 'success', text: 'Accès financier activé pour cet étudiant.' });
      refetch();
    } catch (e: any) {
      const code = e?.data?.code;
      if (code === 'mdp_non_configure') {
        setFeedback({ type: 'error', text: 'Aucun mot de passe configuré. Un administrateur doit le définir dans Paramètres > Général.' });
      } else if (code === 'mdp_incorrect') {
        setFeedback({ type: 'error', text: 'Mot de passe de confirmation incorrect.' });
      } else {
        setFeedback({ type: 'error', text: e?.data?.detail || 'Erreur lors de l\'activation.' });
      }
    }
  };


  const [testMatricule, setTestMatricule] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);

  const handleTestPush = async () => {
    const m = testMatricule.trim().toUpperCase();
    if (!m) return;
    setTestResult(null);
    try {
      const r = await pushTest({ matricule: m }).unwrap();
      if (r.nb_subscriptions_actives === 0) {
        setTestResult(`${m} : aucun appareil abonné. L'étudiant doit cliquer "Activer" sur le banner d'accueil.`);
      } else if (r.nb_pushs_envoyees === 0) {
        setTestResult(`${m} : ${r.nb_subscriptions_actives} abonnement(s) mais 0 push livrée. Vérifier les logs.`);
      } else {
        setTestResult(`${m} : ${r.nb_pushs_envoyees}/${r.nb_subscriptions_actives} push envoyée(s). Vérifier le téléphone.`);
      }
    } catch (e: any) {
      setTestResult(e?.data?.detail || 'Erreur lors du test');
    }
  };

  const messages = data?.results || [];

  // Regroupement par étudiant (matricule). Vue par défaut = "groupé" : on voit
  // tous les sujets/conversations d'un même étudiant ensemble. L'utilisateur
  // peut basculer en vue plate via le bouton ci-dessous.
  const [groupByEtudiant, setGroupByEtudiant] = useState(true);
  const [openMat, setOpenMat] = useState<Set<string>>(new Set());

  const groupes = useMemo(() => {
    if (!groupByEtudiant) return null;
    const map = new Map<string, {
      matricule: string;
      nom_complet: string;
      classe?: string;
      niveau?: string;
      photo_url?: string | null;
      threads: MessageEtudiant[];
      nb_non_lus: number;
      nb_non_traites: number;
      derniere_activite: string;
    }>();
    for (const m of messages) {
      const key = m.matricule;
      let g = map.get(key);
      if (!g) {
        g = {
          matricule: key,
          nom_complet: m.nom_complet || key,
          classe: m.classe,
          niveau: m.niveau,
          photo_url: m.photo_url,
          threads: [],
          nb_non_lus: 0,
          nb_non_traites: 0,
          derniere_activite: m.date_creation,
        };
        map.set(key, g);
      }
      g.threads.push(m);
      if (!m.lu) g.nb_non_lus += 1;
      if (!m.traite) g.nb_non_traites += 1;
      if (m.date_creation > g.derniere_activite) g.derniere_activite = m.date_creation;
    }
    // Trier les threads de chaque groupe (plus récent en premier)
    const arr = Array.from(map.values());
    for (const g of arr) {
      g.threads.sort((a, b) => b.date_creation.localeCompare(a.date_creation));
    }
    // Trier les groupes (étudiant le + récent en premier)
    arr.sort((a, b) => b.derniere_activite.localeCompare(a.derniere_activite));
    return arr;
  }, [messages, groupByEtudiant]);

  const toggleMat = (mat: string) => {
    setOpenMat((prev) => {
      const next = new Set(prev);
      if (next.has(mat)) next.delete(mat);
      else next.add(mat);
      return next;
    });
  };

  const handleSelect = async (m: MessageEtudiant) => {
    setSelected(m);
    setReponse(m.reponse || '');
    setFeedback(null);
    if (!m.lu) {
      try { await marquerLu(m.id).unwrap(); } catch { /* ignore */ }
    }
  };

  const handleRepondre = async () => {
    if (!selected) return;
    const hasAttachment = !!fichierAttache;
    if (!hasAttachment && reponse.trim().length < 5) {
      setFeedback({ type: 'error', text: 'Réponse trop courte (5 caractères min)' });
      return;
    }
    try {
      const isAudio = fichierAttache?.type.startsWith('audio/');
      await repondre({
        id: selected.id,
        reponse: reponse.trim(),
        fichier: fichierAttache || undefined,
        duree_audio_sec: isAudio && recordedDuration > 0 ? recordedDuration : undefined,
      }).unwrap();
      setFeedback({
        type: 'success',
        text: hasAttachment
          ? 'Réponse + pièce jointe envoyées.'
          : 'Réponse envoyée. L\'étudiant recevra une notification.',
      });
      refetch();
      setSelected({ ...selected, traite: true, reponse: reponse.trim() || '[Pièce jointe]' });
      setReponse('');
      setFichierAttache(null);
      setRecordedDuration(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setFeedback({ type: 'error', text: e?.data?.detail || 'Erreur d\'envoi' });
    }
  };

  // Coller une image depuis le presse-papier (Ctrl+V)
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    for (const it of items) {
      if (it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          setFichierAttache(file);
          return;
        }
      }
    }
  };

  // Drag & drop sur le composer
  const [isDragging, setIsDragging] = useState(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('audio/') ||
                 file.type === 'application/pdf')) {
      setFichierAttache(file);
    }
  };

  const handleArchiver = async (m: MessageEtudiant) => {
    if (!window.confirm('Archiver ce message ?')) return;
    try {
      await archiver({ id: m.id, traite: true }).unwrap();
      if (selected?.id === m.id) setSelected(null);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-3">
      {/* Outil diagnostic push */}
      <details className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 flex items-center gap-2 list-none">
          <Bell size={13} className="text-gray-500" />
          Diagnostic notifications push
          <span className="text-gray-400 font-normal text-[10px]">(envoyer une notif test à un étudiant)</span>
        </summary>
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
          <input
            value={testMatricule}
            onChange={(e) => setTestMatricule(e.target.value)}
            placeholder="Matricule (ex: 2IP250019)"
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
          />
          <button
            onClick={handleTestPush}
            disabled={testing || !testMatricule.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
          >
            {testing ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
            Envoyer test
          </button>
          {testResult && (
            <span className="text-xs text-gray-700 dark:text-gray-300 ml-1">{testResult}</span>
          )}
        </div>
      </details>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg">
          {STATUTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatut(s.key)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                statut === s.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {s.label}
              {s.key === 'non_lus' && data && data.nb_non_lus > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-blue-600 text-white rounded-full px-1">
                  {data.nb_non_lus}
                </span>
              )}
              {s.key === 'non_traites' && data && data.nb_non_traites > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-orange-600 text-white rounded-full px-1">
                  {data.nb_non_traites}
                </span>
              )}
              {s.key === 'importants' && data && (data.nb_importants ?? 0) > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-amber-500 text-white rounded-full px-1">
                  {data.nb_importants}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[180px] relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (matricule, sujet, contenu)..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(320px,2fr)_3fr] gap-3">
        {/* Liste */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              <Loader2 className="inline-block animate-spin mr-2" size={14} /> Chargement...
            </div>
          ) : messages.length === 0 ? (
            <div className="p-6 text-center">
              <Inbox className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-400">Aucun message</p>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <div className="px-2 py-1.5 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/60">
                <span>{groupByEtudiant ? `${groupes?.length || 0} étudiant(s)` : `${messages.length} message(s)`}</span>
                <button
                  onClick={() => setGroupByEtudiant((v) => !v)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {groupByEtudiant ? 'Vue plate' : 'Vue groupée'}
                </button>
              </div>

              {groupByEtudiant && groupes ? (
                /* ── Vue groupée par étudiant ── */
                <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {groupes.map((g) => {
                    const isOpen = openMat.has(g.matricule) || g.threads.length === 1;
                    return (
                      <div key={g.matricule}>
                        <button
                          onClick={() => {
                            if (g.threads.length === 1) {
                              handleSelect(g.threads[0]);
                            } else {
                              toggleMat(g.matricule);
                            }
                          }}
                          className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex items-start gap-2.5">
                            <StudentAvatar photoUrl={g.photo_url} nom={g.nom_complet} size={36} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                                <div className={`text-xs ${g.nb_non_lus > 0 ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} truncate flex items-center gap-1`}>
                                  {g.threads.some(t => t.est_important) && <Star size={11} fill="currentColor" className="text-amber-500 flex-shrink-0" />}
                                  {g.nom_complet}
                                </div>
                                <div className="text-[10px] text-gray-400 flex-shrink-0">
                                  {new Date(g.derniere_activite).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                                </div>
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 font-mono">
                                {g.matricule}{g.classe ? ` • ${g.classe}` : ''}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                                <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                  {g.threads.length} sujet{g.threads.length > 1 ? 's' : ''}
                                </span>
                                {g.nb_non_lus > 0 && (
                                  <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded">
                                    {g.nb_non_lus} non lu{g.nb_non_lus > 1 ? 's' : ''}
                                  </span>
                                )}
                                {g.nb_non_traites > 0 && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded">
                                    {g.nb_non_traites} à traiter
                                  </span>
                                )}
                                {g.threads.length > 1 && (
                                  isOpen
                                    ? <ChevronDown size={12} className="text-gray-400 ml-auto" />
                                    : <ChevronRight size={12} className="text-gray-400 ml-auto" />
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                        {g.threads.length > 1 && isOpen && (
                          <div className="bg-gray-50/60 dark:bg-gray-900/40 border-l-2 border-l-gray-300 dark:border-l-gray-600 ml-4">
                            {g.threads.map((m) => (
                              <button
                                key={m.id}
                                onClick={() => handleSelect(m)}
                                className={`w-full text-left p-2.5 pl-4 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${
                                  selected?.id === m.id
                                    ? 'bg-blue-50 dark:bg-blue-900/20'
                                    : 'hover:bg-white dark:hover:bg-gray-800/60'
                                } ${!m.lu ? 'border-l-2 border-l-blue-500' : ''}`}
                              >
                                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                                  <div className={`text-xs ${!m.lu ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} truncate flex items-center gap-1`}>
                                    {m.est_important && <Star size={10} fill="currentColor" className="text-amber-500 flex-shrink-0" />}
                                    {m.sujet || '(sans sujet)'}
                                  </div>
                                  <div className="text-[10px] text-gray-400 flex-shrink-0">
                                    {new Date(m.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                  {m.message}
                                </div>
                                {m.traite && (
                                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-green-700 dark:text-green-400">
                                    <CheckCheck size={10} /> Traité
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ── Vue plate (ancienne) ── */
                <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {messages.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => handleSelect(m)}
                      className={`w-full text-left p-3 transition-colors ${
                        selected?.id === m.id
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      } ${!m.lu ? 'border-l-2 border-l-blue-500' : ''}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <StudentAvatar photoUrl={m.photo_url} nom={m.nom_complet || m.matricule} size={36} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-0.5">
                            <div className={`text-xs ${!m.lu ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} truncate flex items-center gap-1`}>
                              {m.est_important && <Star size={11} fill="currentColor" className="text-amber-500 flex-shrink-0" />}
                              {m.nom_complet || m.matricule}
                            </div>
                            <div className="text-[10px] text-gray-400 flex-shrink-0">
                              {new Date(m.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 font-mono">
                            {m.matricule}{m.classe ? ` • ${m.classe}` : ''}
                          </div>
                          <div className={`text-xs ${!m.lu ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'} truncate`}>
                            {m.sujet || '(sans sujet)'}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {m.message}
                          </div>
                          {m.traite && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-green-700 dark:text-green-400">
                              <CheckCheck size={10} /> Traité
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Détail / réponse */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          {!selected ? (
            <div className="text-sm text-gray-400 text-center py-12">
              Sélectionnez un message à gauche
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <button
                      onClick={() => selected.photo_url && setLightboxUrl(selected.photo_url)}
                      className={`${selected.photo_url ? 'cursor-zoom-in hover:ring-2 hover:ring-blue-400 rounded-full' : ''} transition-all`}
                      title={selected.photo_url ? 'Voir la photo en grand' : ''}
                    >
                      <StudentAvatar photoUrl={selected.photo_url} nom={selected.nom_complet || selected.matricule} size={48} />
                    </button>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                        {selected.nom_complet || selected.matricule}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {selected.matricule}{selected.classe ? ` • ${selected.classe}` : ''}{selected.niveau ? ` • ${selected.niveau}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleImportant(selected)}
                      className={`p-1.5 rounded transition-colors ${
                        selected.est_important
                          ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                          : 'text-gray-400 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                      title={selected.est_important ? 'Retirer le marqueur important' : 'Marquer comme important'}
                    >
                      <Star size={16} fill={selected.est_important ? 'currentColor' : 'none'} />
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowQuickMenu(v => !v)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                        title="Actions rapides sur cet étudiant"
                      >
                        <MoreHorizontal size={14} /> Actions
                      </button>
                      {showQuickMenu && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowQuickMenu(false)} />
                          {/* Fix 2026-09-04 : sous-menus par semestre (S1 impair / S2 pair) */}
                          <div className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                            {/* Impression relevé */}
                            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1.5">
                              <Printer size={11} className="text-blue-600" /> Imprimer relevé
                            </div>
                            <div className="grid grid-cols-2 gap-1 px-2 pb-1">
                              <button onClick={() => openReleve(1)}
                                className="px-2 py-1.5 text-[11px] font-semibold text-blue-700 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                {labelSemestre(selected.niveau, 1)} <span className="opacity-60 font-normal">(impair)</span>
                              </button>
                              <button onClick={() => openReleve(2)}
                                className="px-2 py-1.5 text-[11px] font-semibold text-blue-700 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                {labelSemestre(selected.niveau, 2)} <span className="opacity-60 font-normal">(pair)</span>
                              </button>
                            </div>

                            <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

                            {/* Modifier / recalculer */}
                            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1.5">
                              <FileText size={11} className="text-emerald-600" /> Modifier notes / recalculer
                            </div>
                            <div className="grid grid-cols-2 gap-1 px-2 pb-1">
                              <button onClick={() => openActionRapide(1)}
                                className="px-2 py-1.5 text-[11px] font-semibold text-emerald-700 border border-emerald-200 dark:border-emerald-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
                                {labelSemestre(selected.niveau, 1)} <span className="opacity-60 font-normal">(impair)</span>
                              </button>
                              <button onClick={() => openActionRapide(2)}
                                className="px-2 py-1.5 text-[11px] font-semibold text-emerald-700 border border-emerald-200 dark:border-emerald-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
                                {labelSemestre(selected.niveau, 2)} <span className="opacity-60 font-normal">(pair)</span>
                              </button>
                            </div>

                            <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

                            {/* Rachat intelligent */}
                            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1.5">
                              <Calculator size={11} className="text-indigo-600" /> Rachat intelligent
                            </div>
                            <div className="grid grid-cols-2 gap-1 px-2 pb-1">
                              <button onClick={() => openRachat(1)}
                                className="px-2 py-1.5 text-[11px] font-semibold text-indigo-700 border border-indigo-200 dark:border-indigo-800 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                {labelSemestre(selected.niveau, 1)} <span className="opacity-60 font-normal">(impair)</span>
                              </button>
                              <button onClick={() => openRachat(2)}
                                className="px-2 py-1.5 text-[11px] font-semibold text-indigo-700 border border-indigo-200 dark:border-indigo-800 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                {labelSemestre(selected.niveau, 2)} <span className="opacity-60 font-normal">(pair)</span>
                              </button>
                            </div>

                            <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                            <button
                              onClick={() => { setShowQuickMenu(false); setShowAccesModal(true); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                            >
                              <Unlock size={14} className="text-orange-600" />
                              Activer l'accès financier
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(selected.date_creation).toLocaleString('fr-FR')}
                    </div>
                  </div>
                </div>
                {selected.sujet && (
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {selected.sujet}
                  </div>
                )}
              </div>

              <ChatHistory threadId={selected.id} photoUrl={selected.photo_url} nomComplet={selected.nom_complet || selected.matricule} />

              {/* Contexte : autres conversations du même étudiant. Aide la scolarité
                  à voir l'historique avant de répondre (le message courant peut être
                  la continuité d'une autre conversation). */}
              {(() => {
                const autres = messages.filter(
                  m => m.matricule === selected.matricule && m.id !== selected.id,
                );
                if (autres.length === 0) return null;
                autres.sort((a, b) => b.date_creation.localeCompare(a.date_creation));
                return (
                  <div className="border border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5">
                        <Inbox size={12} />
                        Autres conversations avec cet étudiant ({autres.length})
                      </div>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {autres.slice(0, 8).map(m => (
                        <button
                          key={m.id}
                          onClick={() => handleSelect(m)}
                          className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors flex items-start justify-between gap-2 group"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-gray-900 dark:text-gray-100 truncate font-medium">
                              {m.sujet || '(Sans sujet)'}
                              {!m.traite && (
                                <span className="ml-1.5 inline-block text-[9px] font-bold text-amber-700 dark:text-amber-300 px-1 rounded bg-amber-200 dark:bg-amber-900/40">
                                  EN COURS
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 truncate text-[10px]">
                              {m.message.slice(0, 80)}
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-400 flex-shrink-0">
                            {new Date(m.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                          </div>
                        </button>
                      ))}
                      {autres.length > 8 && (
                        <div className="text-[10px] text-gray-500 text-center pt-1">
                          + {autres.length - 8} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`relative ${isDragging ? 'ring-2 ring-blue-400 ring-offset-2 rounded-lg' : ''}`}
              >
                {isDragging && (
                  <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-400 rounded-lg z-10 flex items-center justify-center pointer-events-none">
                    <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                      Déposez l'image ici
                    </div>
                  </div>
                )}
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {selected.reponse ? 'Nouvelle réponse' : 'Répondre'}
                </label>

                {/* Preview de la pièce jointe */}
                {fichierAttache && (
                  <div className="mb-2 inline-flex items-start gap-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    {fichierPreview ? (
                      <img src={fichierPreview} alt=""
                        className="w-20 h-20 object-cover rounded cursor-zoom-in"
                        onClick={() => setLightboxUrl(fichierPreview)} />
                    ) : (
                      <div className="w-20 h-20 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center text-gray-500">
                        <Paperclip size={24} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                        {fichierAttache.name}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {Math.round(fichierAttache.size / 1024)} KB · {fichierAttache.type}
                      </div>
                      <button
                        onClick={() => {
                          setFichierAttache(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="mt-1 text-[10px] text-red-600 hover:text-red-800 inline-flex items-center gap-0.5">
                        <X size={10} /> Retirer
                      </button>
                    </div>
                  </div>
                )}

                <textarea
                  value={reponse}
                  onChange={(e) => setReponse(e.target.value)}
                  onPaste={handlePaste}
                  rows={5}
                  placeholder="Votre réponse… (Ctrl+V pour coller une image, glisser-déposer un fichier)"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                {feedback && (
                  <div className={`mt-2 text-xs ${
                    feedback.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    {feedback.text}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 mt-3">
                  <div className="flex items-center gap-2">
                    <input ref={fileInputRef} type="file"
                      accept="image/*,audio/*,application/pdf,.doc,.docx"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setFichierAttache(f);
                      }}
                      className="hidden" />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Joindre une image, audio ou fichier"
                    >
                      <ImageIcon size={14} /> Joindre
                    </button>
                    {!recording ? (
                      <button
                        type="button"
                        onClick={startRecording}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Enregistrer un message vocal"
                      >
                        <Mic size={14} /> Vocal
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopRecording}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors animate-pulse"
                        title="Arrêter l'enregistrement"
                      >
                        <Square size={12} fill="currentColor" /> Stop ({recordedDuration}s)
                      </button>
                    )}
                    <button
                      onClick={() => handleArchiver(selected)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                    >
                      <Archive size={14} /> Archiver
                    </button>
                  </div>
                  <button
                    onClick={handleRepondre}
                    disabled={envoyantReponse}
                    className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    {envoyantReponse ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {envoyantReponse ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Activer accès financier */}
      {showAccesModal && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowAccesModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Unlock size={16} className="text-orange-600" />
                Activer l'accès financier
              </h3>
              <button onClick={() => setShowAccesModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-gray-100">{selected.nom_complet || selected.matricule}</span>
                {' '}<span className="font-mono text-gray-500">({selected.matricule})</span>
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-2.5">
                L'étudiant pourra accéder à son portail (notes, relevés, copies) même s'il n'est pas à jour de ses paiements.
                Cette dérogation est tracée dans le journal d'accès.
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Raison de l'activation *
                </label>
                <textarea
                  value={raisonAcces}
                  onChange={(e) => setRaisonAcces(e.target.value)}
                  rows={3}
                  placeholder="Ex : Accord paiement échelonné, contrat signé, dérogation directrice…"
                  className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              {/* Fix 2026-09-06 : mot de passe de confirmation admin */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  🔒 Mot de passe de confirmation *
                </label>
                <input
                  type="password"
                  value={mdpAcces}
                  onChange={(e) => setMdpAcces(e.target.value)}
                  placeholder="Mot de passe défini par l'admin"
                  className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  Défini dans Paramètres → Général → « Confirmation Forcer l'accès ».
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => { setShowAccesModal(false); setRaisonAcces(''); setMdpAcces(''); }}
                  className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  Annuler
                </button>
                <button
                  onClick={handleForcerAcces}
                  disabled={forcantAcces || raisonAcces.trim().length < 5 || !mdpAcces.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-xs font-medium rounded"
                >
                  {forcantAcces ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />}
                  Activer l'accès
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal action rapide : modifier notes + recalculer.
          Fix 2026-09-04 : semestre dynamique (S1 ou S2) selon choix agent. */}
      {showActionRapide && selected && etudiantIdSelected && (
        <ActionRapideDeliberationModal
          open
          etudiantId={etudiantIdSelected}
          matricule={selected.matricule}
          nom={selected.nom_complet || selected.matricule}
          semestre={actionSemestre}
          anneeAcademique="2025-2026"
          moyenneActuelle={null}
          onClose={() => { setShowActionRapide(false); setEtudiantIdSelected(null); }}
          onApplied={() => refetch()}
        />
      )}

      {/* Lightbox plein écran pour les images (photo étudiant ou image jointe) */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
            aria-label="Fermer"
          >
            <X size={24} />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default MessagesEtudiantsView;


/** Affichage chronologique du fil de conversation : étudiant + scolarité.
 * Récupère tous les posts via /admin/messages-etudiants/{id}/.
 */
function fmtMoney(s: string): string {
  const n = parseFloat(s);
  if (isNaN(n) || n === 0) return '0';
  return new Intl.NumberFormat('fr-FR').format(n);
}

const ChatHistory: React.FC<{ threadId: number; photoUrl?: string | null; nomComplet?: string }> = ({ threadId, photoUrl, nomComplet }) => {
  // Rechargé à chaque ouverture : l'état d'accès dépend des périodes requises, modifiables à tout moment.
  const { data, isLoading, refetch } = useGetMessageEtudiantThreadQuery(threadId, { refetchOnMountOrArgChange: true });
  const [supprimerPost] = useSupprimerPostMessageEtudiantMutation();
  const [lightbox, setLightbox] = useState<string | null>(null);

  const handleDelete = async (postId: number) => {
    if (!window.confirm('Supprimer ce message ? Cette action est irréversible.')) return;
    try {
      await supprimerPost(postId).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (isLoading) {
    return (
      <div className="text-xs text-gray-400 p-3 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
        <Loader2 size={12} className="inline-block animate-spin mr-1" /> Chargement de la conversation...
      </div>
    );
  }
  if (!data || !data.messages.length) return null;

  const acces = data.acces;
  const paiements = data.paiements || [];
  const photo = data.thread.photo_url || photoUrl || null;
  const nom = data.thread.nom_complet || nomComplet || '';
  // Fix 2026-09-04 : 2 semestres au lieu d'un seul (S1 + S2).
  const moyennes: any[] = (data.thread as any).moyennes || [];
  const moyenneCourante = data.thread.moyenne_courante; // legacy fallback
  const moyenneLabel = data.thread.moyenne_label;

  return (
    <div className="space-y-3">
      {/* Badges moyennes — un par semestre absolu (L1→S1/S2, L2→S3/S4…) */}
      {moyennes.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-gray-400">
            Moyennes
          </span>
          {moyennes.map((m) => (
            <div key={`${m.semestre}-${m.semestre_relatif}`}
              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-lg border ${
                m.valide
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700'
                  : m.doit_rattraper
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                    : 'bg-slate-50 dark:bg-gray-800 border-slate-200 dark:border-gray-700'
              }`}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-gray-400">
                S{m.semestre}
              </span>
              <span className={`text-base font-bold font-mono ${
                m.valeur >= 10 ? 'text-emerald-700 dark:text-emerald-400'
                : 'text-amber-700 dark:text-amber-400'
              }`}>
                {m.valeur !== null ? m.valeur.toFixed(2) : '—'}
                <span className="text-[10px] text-gray-500 font-normal">/20</span>
              </span>
              {m.mention && (
                <span className="text-[10px] text-gray-500 dark:text-gray-400 hidden sm:inline">
                  · {m.mention}
                </span>
              )}
              {m.valide && m.session === 'rattrapage' && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-semibold uppercase">
                  R
                </span>
              )}
              {m.valide && m.session !== 'rattrapage' && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-semibold uppercase">
                  Validé
                </span>
              )}
              {!m.valide && m.doit_rattraper && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-semibold uppercase">
                  À rattr.
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Fallback legacy si le backend ne renvoie pas encore moyennes[] */
        moyenneCourante !== null && moyenneCourante !== undefined && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700 dark:text-indigo-300">
              Moyenne actuelle
            </span>
            <span className={`text-lg font-bold font-mono ${
              moyenneCourante >= 10 ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-amber-600 dark:text-amber-400'
            }`}>
              {moyenneCourante.toFixed(2)}<span className="text-xs text-gray-500">/20</span>
            </span>
            {moyenneLabel && (
              <span className="text-xs text-gray-500 dark:text-gray-400">· {moyenneLabel}</span>
            )}
          </div>
        )
      )}

      {/* Panneau état paiements */}
      {(acces || paiements.length > 0) && (
        <div className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Situation financière
              </span>
              {acces && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                    acces.mode === 'a_jour' || acces.mode === 'sous_contrat' || acces.mode === 'force_admin'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : acces.mode === 'bloque_dette'
                      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}
                >
                  {acces.mode === 'a_jour' && 'À jour'}
                  {acces.mode === 'sous_contrat' && `Sous contrat${acces.contrat_type ? ` (${acces.contrat_type})` : ''}`}
                  {acces.mode === 'force_admin' && 'Activé manuellement'}
                  {acces.mode === 'bloque_dette' && 'En dette'}
                  {acces.mode === 'bloque_absent' && (paiements.some(p => p.absente)
                    ? `Non enregistré : ${paiements.filter(p => p.absente).map(p => p.periode_label).join(', ')}`
                    : 'Aucun paiement enregistré')}
                  {!['a_jour','sous_contrat','force_admin','bloque_dette','bloque_absent'].includes(acces.mode) && acces.mode}
                </span>
              )}
            </div>
            {acces && acces.mode === 'bloque_dette' && parseFloat(acces.solde_total) > 0 && (
              <span className="text-xs text-red-600 dark:text-red-400 font-mono">
                Solde dû : {fmtMoney(acces.solde_total)} FCFA
              </span>
            )}
          </div>
          {acces?.contrat_remarque && (
            <div className="text-[11px] text-gray-600 dark:text-gray-400 italic mb-2">
              {acces.contrat_remarque}
            </div>
          )}
          {paiements.length === 0 ? (
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              Aucun paiement enregistré pour cet étudiant.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
              {paiements.map((p) => (
                <div
                  key={p.periode}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] ${
                    p.est_a_jour
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50'
                  }`}
                >
                  {p.est_a_jour ? (
                    <CheckCheck size={11} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                  ) : (
                    <X size={11} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {p.periode_label}
                    </div>
                    {!p.est_a_jour && parseFloat(p.solde) > 0 && (
                      <div className="text-[9px] text-red-600 dark:text-red-400 font-mono">
                        {fmtMoney(p.solde)}
                      </div>
                    )}
                    {p.saisie_manuelle && (
                      <div className="text-[9px] text-blue-600 dark:text-blue-400">caisse</div>
                    )}
                    {p.absente && (
                      <div className="text-[9px] text-red-600 dark:text-red-400">non enregistré</div>
                    )}
                    {p.requise === false && (
                      <div className="text-[9px] text-gray-500 dark:text-gray-400">non requise</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Conversation */}
      <div className="space-y-2 max-h-[40vh] overflow-y-auto bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded p-3">
        {data.messages.map((m) => {
          const isStudent = m.expediteur === 'etudiant';
          const isPost = typeof m.id === 'number';  // les posts ont id numérique, init/rep sont strings
          return (
            <div
              key={m.id}
              className={`flex items-end gap-2 ${isStudent ? 'justify-start' : 'justify-end'}`}
            >
              {isStudent && (
                <button onClick={() => photo && setLightbox(photo)}
                  className={photo ? 'cursor-zoom-in' : ''}
                  title={photo ? 'Voir la photo en grand' : ''}>
                  <StudentAvatar photoUrl={photo} nom={nom} size={26} />
                </button>
              )}
              <div className={`max-w-[80%] flex flex-col ${isStudent ? 'items-start' : 'items-end'}`}>
                <div
                  className={`rounded-lg px-3 py-2 text-xs ${
                    m.supprime
                      ? 'bg-gray-100 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 italic'
                      : isStudent
                        ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
                        : 'bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100'
                  }`}
                >
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                    {isStudent ? 'Étudiant' : (m.expediteur_nom || 'Scolarité')}
                    {' • '}
                    {new Date(m.date_creation).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {/* Rendu pièce jointe selon le type */}
                  {!m.supprime && m.type === 'image' && m.fichier_url && (
                    <img src={m.fichier_url}
                      alt={m.fichier_nom_original || 'image'}
                      onClick={() => setLightbox(m.fichier_url!)}
                      className="max-w-[260px] max-h-[260px] rounded mb-1 cursor-zoom-in object-cover" />
                  )}
                  {!m.supprime && m.type === 'audio' && m.fichier_url && (
                    <div className="mb-1">
                      <audio controls src={m.fichier_url} className="max-w-[260px]" />
                      {m.duree_audio_sec ? (
                        <div className="text-[10px] text-gray-500">{m.duree_audio_sec}s</div>
                      ) : null}
                    </div>
                  )}
                  {!m.supprime && m.type === 'fichier' && m.fichier_url && (
                    <a href={m.fichier_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2 py-1 mb-1 bg-white/50 dark:bg-black/20 rounded text-xs hover:underline">
                      <Download size={12} />
                      {m.fichier_nom_original || 'Télécharger'}
                    </a>
                  )}
                  <div className="whitespace-pre-wrap break-words leading-relaxed">
                    {m.supprime
                      ? `Message supprimé${m.supprime_par ? ` par ${m.supprime_par === 'etudiant' ? "l'étudiant" : 'la scolarité'}` : ''}`
                      : m.contenu}
                  </div>
                </div>
                <div className={`flex items-center gap-2 mt-0.5 px-1 text-[10px] ${isStudent ? '' : 'flex-row-reverse'}`}>
                  {/* Accusé de lecture pour les messages scolarité */}
                  {!isStudent && !m.supprime && (
                    m.lu_par_destinataire ? (
                      <span className="text-blue-600 dark:text-blue-400 inline-flex items-center gap-0.5" title={m.date_lecture ? `Lu le ${new Date(m.date_lecture).toLocaleString('fr-FR')}` : 'Lu'}>
                        <CheckCheck size={11} /> Lu{m.date_lecture ? ` ${new Date(m.date_lecture).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                      </span>
                    ) : (
                      <span className="text-gray-400 inline-flex items-center gap-0.5" title="Pas encore lu">
                        <Check size={11} /> Envoyé
                      </span>
                    )
                  )}
                  {/* Bouton suppression sur les posts scolarité non supprimés */}
                  {m.peut_supprimer && isPost && (
                    <button
                      onClick={() => handleDelete(m.id as number)}
                      className="text-red-500 hover:text-red-700 dark:hover:text-red-400 inline-flex items-center gap-0.5"
                      title="Supprimer ce message"
                    >
                      <Trash2 size={11} /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox vue plein écran image */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white">
            <X size={24} />
          </button>
          <img src={lightbox} alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};
